import React, { useState, useEffect, useRef } from "react";
import { Result } from "../../types";
import PDFExport from "./TechnologyPDFExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Radar, Pie, Line } from "react-chartjs-2";
import {
  DataObject,
  Speed,
  Analytics,
  LocalActivity,
  AutoAwesome,
  Settings,
  Chat,
  Timeline,
  Campaign,
  Build,
  Celebration,
  Assignment,
  AttachMoney,
  Business,
  GpsFixed,
  Assessment,
  Code,
  Security,
  TrendingUp,
  AccountBalance,
  SupportAgent,
  Insights,
  School,
  BusinessCenter,
  Groups,
} from "@mui/icons-material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

interface ResultsPageProps {
  results: Result[];
  onRestart: () => void;
}

const ResultsPage: React.FC<ResultsPageProps> = ({ results, onRestart }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTab, setSelectedTab] = useState("recommendations");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 4 ? prev + 1 : prev));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update recommendations generation function to better match questions
  const generateDetailedRecommendations = () => {
    const recommendations = [];
    const answers = results.map((r) => r.answer.toLowerCase());

    // Strategic Planning & Roadmap
    if (
      answers.some(
        (a) =>
          a.includes("timeline") ||
          a.includes("milestones") ||
          a.includes("plan")
      )
    ) {
      recommendations.push({
        category: "Strategic Planning",
        title: "Technology Roadmap Development",
        description:
          "Comprehensive technology roadmap with clear timelines, milestones, and strategic alignment to business goals.",
        priority: "High",
        icon: <Timeline />,
        benefits: [
          "Clear technology implementation timeline",
          "Aligned milestones with business objectives",
          "Risk mitigation and contingency planning",
          "Measurable success criteria and KPIs",
        ],
        implementation: "4-6 weeks",
        roi: "300% within 12 months",
        score: 95,
      });
    }

    // Process Improvement
    if (
      answers.some(
        (a) =>
          a.includes("improve") ||
          a.includes("processes") ||
          a.includes("smoother") ||
          a.includes("manual")
      )
    ) {
      recommendations.push({
        category: "Process Optimization",
        title: "Operational Efficiency Assessment",
        description:
          "Deep dive analysis of current processes to identify automation opportunities and efficiency improvements.",
        priority: "High",
        icon: <TrendingUp />,
        benefits: [
          "Identify process bottlenecks and inefficiencies",
          "Map automation opportunities",
          "Reduce manual work by 60-80%",
          "Improve operational productivity",
        ],
        implementation: "6-10 weeks",
        roi: "250% within 8 months",
        score: 92,
      });
    }

    // Assessment & Evaluation
    if (
      answers.some(
        (a) =>
          a.includes("evaluate") ||
          a.includes("strengths") ||
          a.includes("challenges") ||
          a.includes("capabilities")
      )
    ) {
      recommendations.push({
        category: "Digital Capability Assessment",
        title: "Technology Maturity Evaluation",
        description:
          "Comprehensive assessment of your organization's digital capabilities, strengths, and improvement areas.",
        priority: "Medium",
        icon: <Assessment />,
        benefits: [
          "Understand current technology maturity level",
          "Identify skill gaps and training needs",
          "Benchmark against industry standards",
          "Create targeted improvement plans",
        ],
        implementation: "4-6 weeks",
        roi: "180% within 10 months",
        score: 88,
      });
    }

    // Budget & Investment Planning
    if (
      answers.some(
        (a) =>
          a.includes("budget") ||
          a.includes("investments") ||
          a.includes("cost") ||
          a.includes("expenses")
      )
    ) {
      recommendations.push({
        category: "Investment Planning",
        title: "Technology Budget & ROI Analysis",
        description:
          "Strategic technology investment planning with detailed budget allocation and ROI projections.",
        priority: "High",
        icon: <AccountBalance />,
        benefits: [
          "Optimize technology spending allocation",
          "Clear ROI projections for investments",
          "Phased implementation to manage cash flow",
          "Risk-adjusted investment recommendations",
        ],
        implementation: "4-6 weeks",
        roi: "220% within 12 months",
        score: 90,
      });
    }

    // Customer Experience Focus
    if (
      answers.some((a) => a.includes("customer") || a.includes("experience"))
    ) {
      recommendations.push({
        category: "Customer Experience",
        title: "Digital Customer Experience Strategy",
        description:
          "Technology solutions designed to enhance customer interactions and satisfaction across all touchpoints.",
        priority: "Medium",
        icon: <SupportAgent />,
        benefits: [
          "Improved customer satisfaction scores",
          "Seamless omnichannel experiences",
          "Reduced customer service response times",
          "Increased customer retention rates",
        ],
        implementation: "8-12 weeks",
        roi: "200% within 9 months",
        score: 87,
      });
    }

    // Data & Decision Making
    if (
      answers.some(
        (a) =>
          a.includes("data") ||
          a.includes("decision") ||
          a.includes("dashboard") ||
          a.includes("reports")
      )
    ) {
      recommendations.push({
        category: "Data & Analytics",
        title: "Business Intelligence Implementation",
        description:
          "Comprehensive data strategy with dashboards, reporting, and analytics capabilities for data-driven decisions.",
        priority: "Medium",
        icon: <Insights />,
        benefits: [
          "Real-time business performance dashboards",
          "Automated reporting and analytics",
          "Data-driven decision making capabilities",
          "Improved forecast accuracy by 40%",
        ],
        implementation: "6-10 weeks",
        roi: "190% within 7 months",
        score: 85,
      });
    }

    // Change Management & Training
    if (
      answers.some(
        (a) =>
          a.includes("staff") ||
          a.includes("learning") ||
          a.includes("training")
      )
    ) {
      recommendations.push({
        category: "Change Management",
        title: "Technology Adoption & Training Program",
        description:
          "Comprehensive change management program to ensure successful technology adoption across your organization.",
        priority: "Medium",
        icon: <School />,
        benefits: [
          "Smooth technology transitions",
          "Reduced resistance to change",
          "Improved user adoption rates",
          "Comprehensive training programs",
        ],
        implementation: "8-12 weeks",
        roi: "160% within 12 months",
        score: 82,
      });
    }

    // Security & Privacy
    if (
      answers.some(
        (a) =>
          a.includes("privacy") ||
          a.includes("security") ||
          a.includes("controls") ||
          a.includes("storage")
      )
    ) {
      recommendations.push({
        category: "Security & Compliance",
        title: "Data Security & Privacy Framework",
        description:
          "Robust security framework with privacy controls, compliance measures, and reliable data storage solutions.",
        priority: "High",
        icon: <Security />,
        benefits: [
          "Enhanced data security and privacy",
          "Regulatory compliance assurance",
          "Risk mitigation and protection",
          "Secure data storage and backup systems",
        ],
        implementation: "6-8 weeks",
        roi: "170% within 8 months",
        score: 89,
      });
    }

    return recommendations;
  };

  const recommendations = generateDetailedRecommendations();
  const categories = [...new Set(recommendations.map((r) => r.category))];

  // Remove this unused priorityData variable (around line 237):
  // const priorityData = {
  //   labels: [
  //     "High Priority (Immediate Implementation)",
  //     "Medium Priority (Phase 2)",
  //     "Low Priority (Future Consideration)",
  //   ],
  //   datasets: [
  //     {
  //       data: [
  //         recommendations.filter((r) => r.priority === "High").length,
  //         recommendations.filter((r) => r.priority === "Medium").length,
  //         recommendations.filter((r) => r.priority === "Low").length,
  //       ],
  //       backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
  //       borderWidth: 0,
  //       hoverOffset: 10,
  //     },
  //   ],
  // };

  // Keep the other chart data objects that are actually being used:
  const categoryData = {
    labels: categories,
    datasets: [
      {
        label: "Number of Recommended Features",
        data: categories.map(
          (cat) => recommendations.filter((r) => r.category === cat).length
        ),
        backgroundColor: "rgba(99, 102, 241, 0.8)",
        borderColor: "#6366f1",
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const businessImpactData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Business Impact Score (0-100)",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.3)",
        borderColor: "#10b981",
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#10b981",
      },
    ],
  };

  const roiTimelineData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Expected ROI (%)",
        data: recommendations.map((r) => {
          // Extract ROI percentage from strings like "150% within 6 months"
          const roiMatch = r.roi.match(/(\d+)%/);
          return roiMatch ? parseInt(roiMatch[1]) : 100;
        }),
        backgroundColor: "rgba(245, 158, 11, 0.6)",
        borderColor: "#f59e0b",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: "Implementation Score",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.6)",
        borderColor: "#10b981",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Priority distribution based on actual user responses
  const responseBasedPriority = {
    labels: [
      "Immediate Impact (High ROI)",
      "Strategic Growth (Medium ROI)",
      "Future Enhancement (Lower ROI)",
    ],
    datasets: [
      {
        data: [
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 120 && roiValue < 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue < 120;
          }).length,
        ],
        backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  return (
    <div
      ref={resultsRef}
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        minHeight: "100vh",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Enhanced Header with responsive design */}
        <div
          className={animationStep >= 0 ? "fade-in" : ""}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "2.5rem" : "4rem",
              marginBottom: "1rem",
            }}
          >
            <Celebration
              sx={{ fontSize: isMobile ? "2.5rem" : "4rem", color: "white" }}
            />
          </div>
          <h1
            style={{
              fontFamily: "var(--font-headline)",
              fontSize: isMobile
                ? "var(--font-size-headline-mobile)"
                : "var(--font-size-headline)",
              fontWeight: "800",
              marginBottom: "1rem",
              background:
                "linear-gradient(35deg,rgb(215, 227, 255) 0%,rgb(155, 188, 255) 30%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "1.2",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Your Strategic Technology Roadmap
          </h1>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: isMobile
                ? "var(--font-size-body-mobile)"
                : "var(--font-size-body)",
              opacity: 0.9,
              maxWidth: "700px",
              margin: "0 auto 2rem",
              lineHeight: "1.6",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Based on your responses, we've created a comprehensive strategic
            technology plan tailored to your business goals with detailed
            implementation guidance and ROI projections for maximum strategic
            impact.
          </p>

          {/* Key Statistics - Responsive Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: isMobile ? "0.75rem" : "1rem",
              maxWidth: "800px",
              margin: "0 auto",
              padding: isMobile ? "0 0.5rem" : "0",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Assignment
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                {recommendations.length}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Strategic Planning Features
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Speed
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                4-16 weeks
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Implementation
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                gridColumn: isMobile ? "1 / -1" : "auto",
              }}
            >
              <AttachMoney
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                150%+
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Expected ROI within 6-12 months
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Responsive */}
        <div
          className={animationStep >= 1 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            flexWrap: "wrap",
            gap: isMobile ? "0.5rem" : "0",
          }}
        >
          {["recommendations", "analytics", "your-responses"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              style={{
                padding: isMobile ? "0.75rem 1rem" : "1rem 2rem",
                margin: isMobile ? "0" : "0 0.5rem",
                backgroundColor:
                  selectedTab === tab
                    ? "rgba(255, 255, 255, 0.95)"
                    : "rgba(255, 255, 255, 0.1)",
                color: selectedTab === tab ? "#1f2937" : "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1.1rem",
                fontWeight: "600",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                textTransform: "capitalize",
                minWidth: isMobile ? "auto" : "120px",
              }}
            >
              {tab === "your-responses" ? "Your Responses" : tab}
            </button>
          ))}
        </div>

        {/* Content based on selected tab */}
        {selectedTab === "recommendations" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Detailed Recommendations - Responsive Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {recommendations.map((recommendation, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "20px",
                    padding: isMobile ? "1.5rem" : "2.5rem",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transition: "transform 0.3s ease",
                    transitionDelay: `${index * 0.1}s`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(-8px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {/* Priority Badge - Responsive */}
                  <div
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      padding: isMobile ? "0.3rem 0.75rem" : "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: isMobile ? "0.7rem" : "0.8rem",
                      fontWeight: "600",
                      backgroundColor:
                        recommendation.priority === "High"
                          ? "#fee2e2"
                          : recommendation.priority === "Medium"
                          ? "#fef3c7"
                          : "#d1fae5",
                      color:
                        recommendation.priority === "High"
                          ? "#dc2626"
                          : recommendation.priority === "Medium"
                          ? "#d97706"
                          : "#059669",
                    }}
                  >
                    {recommendation.priority} Priority
                  </div>

                  {/* Header - Responsive */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "1.5rem",
                      flexDirection: isMobile ? "column" : "row",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "2rem" : "3rem",
                        marginRight: isMobile ? "0" : "1rem",
                        marginBottom: isMobile ? "1rem" : "0",
                        color: "#c9a750ff",
                      }}
                    >
                      {React.cloneElement(recommendation.icon, {
                        sx: { fontSize: isMobile ? "2rem" : "3rem" },
                      })}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#c9a750ff",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {recommendation.category}
                      </div>
                      <h3
                        style={{
                          color: "#1f2937",
                          margin: 0,
                          fontSize: isMobile ? "1.2rem" : "1.4rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {recommendation.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: "1.6",
                      marginBottom: "1.5rem",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                    }}
                  >
                    {recommendation.description}
                  </p>

                  {/* Benefits - Responsive Grid */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.75rem",
                        fontSize: "1rem",
                      }}
                    >
                      Key Benefits:
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(2, 1fr)",
                        gap: "0.5rem",
                      }}
                    >
                      {recommendation.benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                            color: "#4b5563",
                          }}
                        >
                          <span
                            style={{ color: "#10b981", marginRight: "0.5rem" }}
                          >
                            ✓
                          </span>
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Implementation Details - Responsive */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "1rem",
                      padding: "1rem",
                      backgroundColor: "rgba(201, 167, 80, 0.1)",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Timeline
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        {recommendation.implementation}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Expected ROI
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#059669",
                        }}
                      >
                        {recommendation.roi}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Impact Score
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#c9a750ff",
                        }}
                      >
                        {recommendation.score}/100
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === "analytics" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Charts Grid - Responsive */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
              className="analytics-container"
            >
              {/* Strategic Goals Priority - Horizontal Bar Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="strategic-goals"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <BusinessCenter
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Strategic Goals Priority
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Your main technology objectives ranked by importance
                </p>

                <Bar
                  data={{
                    labels: [
                      "Timeline & Milestones",
                      "Process Improvement",
                      "Capability Assessment",
                      "Investment Planning",
                    ],
                    datasets: [
                      {
                        label: "Strategic Priority Score",
                        data: [
                          // Calculate scores based on user answers
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("timeline") ||
                              r.answer.toLowerCase().includes("milestones")
                          )
                            ? 95
                            : 45,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("improve") ||
                              r.answer.toLowerCase().includes("processes")
                          )
                            ? 90
                            : 40,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("evaluate") ||
                              r.answer.toLowerCase().includes("capabilities")
                          )
                            ? 85
                            : 35,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("budget") ||
                              r.answer.toLowerCase().includes("investments")
                          )
                            ? 80
                            : 30,
                        ],
                        backgroundColor: [
                          "#3b82f6", // Blue for Timeline & Milestones
                          "#10b981", // Green for Process Improvement
                          "#f59e0b", // Orange for Capability Assessment
                          "#8b5cf6", // Purple for Investment Planning
                        ],
                        borderWidth: 0,
                        borderRadius: 8,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y" as const,
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `Priority Score: ${context.parsed.x}/100`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        max: 100,
                        grid: { display: false },
                      },
                      y: {
                        grid: { display: false },
                      },
                    },
                  }}
                />
              </div>

              {/* Technology Focus Areas - Pie Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="focus-areas"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Assessment
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Technology Focus Areas
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Areas where technology will have the greatest impact
                </p>

                <Pie
                  data={{
                    labels: [
                      "Internal Operations",
                      "Customer Experience",
                      "Data & Analytics",
                      "Cost Optimization",
                    ],
                    datasets: [
                      {
                        data: [
                          // Calculate focus based on user preferences
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("internal") ||
                              r.answer.toLowerCase().includes("operations") ||
                              r.answer.toLowerCase().includes("smoother")
                          )
                            ? 35
                            : 15,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("customer") ||
                              r.answer.toLowerCase().includes("experience")
                          )
                            ? 30
                            : 15,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("data") ||
                              r.answer.toLowerCase().includes("decision") ||
                              r.answer.toLowerCase().includes("dashboards")
                          )
                            ? 25
                            : 15,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("cost") ||
                              r.answer.toLowerCase().includes("expenses") ||
                              r.answer.toLowerCase().includes("reducing")
                          )
                            ? 20
                            : 15,
                        ],
                        backgroundColor: [
                          "#3b82f6", // Blue for Internal Operations
                          "#10b981", // Green for Customer Experience
                          "#f59e0b", // Orange for Data & Analytics
                          "#ef4444", // Red for Cost Optimization
                        ],
                        borderWidth: 0,
                        hoverOffset: 10,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 15 : 20,
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            const total = context.dataset.data.reduce(
                              (a: number, b: number) => a + b,
                              0
                            );
                            const percentage = Math.round(
                              (context.parsed / total) * 100
                            );
                            return `${context.label}: ${percentage}%`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* Organizational Readiness - Radar Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="organizational-readiness"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Groups
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Organizational Readiness
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Your organization's readiness across key strategic factors
                </p>

                <Radar
                  data={{
                    labels: [
                      "Technology Readiness",
                      "Change Management",
                      "Investment Capacity",
                      "Strategic Alignment",
                      "Staff Capability",
                      "Data Maturity",
                    ],
                    datasets: [
                      {
                        label: "Current State",
                        data: [
                          // Calculate readiness scores based on answers
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("ready") ||
                              r.answer.toLowerCase().includes("already have")
                          )
                            ? 85
                            : results.some(
                                (r) =>
                                  r.answer.toLowerCase().includes("curious") ||
                                  r.answer.toLowerCase().includes("exploring")
                              )
                            ? 65
                            : results.some(
                                (r) =>
                                  r.answer.toLowerCase().includes("cautious") ||
                                  r.answer.toLowerCase().includes("proof")
                              )
                            ? 45
                            : 35,

                          results.some((r) =>
                            r.answer.toLowerCase().includes("staff learning")
                          )
                            ? 40
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("aligning")
                              )
                            ? 60
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("cost")
                              )
                            ? 50
                            : 75,

                          results.some((r) =>
                            r.answer
                              .toLowerCase()
                              .includes("strong business return")
                          )
                            ? 90
                            : results.some((r) =>
                                r.answer
                                  .toLowerCase()
                                  .includes("improvement opportunities")
                              )
                            ? 75
                            : results.some((r) =>
                                r.answer
                                  .toLowerCase()
                                  .includes("leadership vision")
                              )
                            ? 85
                            : 65,

                          results.some((r) =>
                            r.answer
                              .toLowerCase()
                              .includes("very—tech should support")
                          )
                            ? 95
                            : results.some((r) =>
                                r.answer
                                  .toLowerCase()
                                  .includes("somewhat—help us define")
                              )
                            ? 70
                            : results.some((r) =>
                                r.answer
                                  .toLowerCase()
                                  .includes("quick, standalone wins")
                              )
                            ? 45
                            : 25,

                          results.some((r) =>
                            r.answer.toLowerCase().includes("training sessions")
                          )
                            ? 85
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("check-ins")
                              )
                            ? 75
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("guidance")
                              )
                            ? 65
                            : 55,

                          results.some((r) =>
                            r.answer.toLowerCase().includes("dashboards")
                          )
                            ? 80
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("reports")
                              )
                            ? 70
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("accuracy")
                              )
                            ? 75
                            : 60,
                        ],
                        backgroundColor: "rgba(16, 185, 129, 0.2)",
                        borderColor: "#10b981",
                        pointBackgroundColor: "#10b981",
                        pointBorderColor: "#fff",
                        pointHoverBackgroundColor: "#fff",
                        pointHoverBorderColor: "#10b981",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          stepSize: 20,
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </div>

              {/* Investment Strategy Matrix - Scatter Plot */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="investment-strategy"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <AttachMoney
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Investment Strategy Matrix
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Strategic initiatives mapped by impact vs investment required
                </p>

                <Line
                  data={{
                    labels: ["Q1", "Q2", "Q3", "Q4", "Year 2", "Year 3"],
                    datasets: [
                      {
                        label: "Strategic Planning",
                        data: [
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("timeline") ||
                              r.answer.toLowerCase().includes("milestones")
                          )
                            ? 85
                            : 40,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("timeline") ||
                              r.answer.toLowerCase().includes("milestones")
                          )
                            ? 90
                            : 45,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("timeline") ||
                              r.answer.toLowerCase().includes("milestones")
                          )
                            ? 95
                            : 50,
                          95,
                          90,
                          85,
                        ],
                        borderColor: "#3b82f6",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        tension: 0.4,
                        fill: true,
                      },
                      {
                        label: "Process Optimization",
                        data: [
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("improve") ||
                              r.answer.toLowerCase().includes("processes")
                          )
                            ? 75
                            : 30,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("improve") ||
                              r.answer.toLowerCase().includes("processes")
                          )
                            ? 85
                            : 40,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("improve") ||
                              r.answer.toLowerCase().includes("processes")
                          )
                            ? 90
                            : 50,
                          85,
                          80,
                          75,
                        ],
                        borderColor: "#10b981",
                        backgroundColor: "rgba(16, 185, 129, 0.1)",
                        tension: 0.4,
                        fill: true,
                      },
                      {
                        label: "Technology Investment",
                        data: [
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("budget") ||
                              r.answer.toLowerCase().includes("investments")
                          )
                            ? 70
                            : 25,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("budget") ||
                              r.answer.toLowerCase().includes("investments")
                          )
                            ? 80
                            : 35,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("budget") ||
                              r.answer.toLowerCase().includes("investments")
                          )
                            ? 85
                            : 45,
                          90,
                          95,
                          100,
                        ],
                        borderColor: "#f59e0b",
                        backgroundColor: "rgba(245, 158, 11, 0.1)",
                        tension: 0.4,
                        fill: true,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 10 : 15,
                          font: {
                            size: isMobile ? 9 : 11,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.dataset.label}: ${context.parsed.y}% completion`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: true,
                          text: "Strategic Value (%)",
                        },
                      },
                      x: {
                        title: {
                          display: true,
                          text: "Implementation Timeline",
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Chart Explanations - Updated for Strategic Planning */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2rem",
                backdropFilter: "blur(10px)",
                marginBottom: "2rem",
              }}
            >
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: "#1f2937",
                  fontSize: isMobile ? "1.2rem" : "1.3rem",
                }}
              >
                📖 Strategic Planning & Implementation Analytics
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    🎯 Strategic Goals Priority
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Shows your primary technology objectives ranked by
                    importance. Higher scores indicate areas that should be
                    prioritized in your strategic roadmap.
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    🎯 Technology Focus Areas
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Distribution of technology initiatives across key business
                    areas. Larger segments indicate where technology will have
                    the greatest strategic impact.
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    📊 Organizational Readiness
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Assessment of your organization's strategic readiness across
                    key factors. Higher scores indicate areas where you're
                    well-prepared for technology initiatives.
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    💰 Investment Strategy Timeline
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Strategic value delivery over time across different
                    initiative types. Shows how strategic investments compound
                    and deliver value over your roadmap timeline.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Your Responses section would follow similar responsive patterns */}
        {selectedTab === "your-responses" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Response content with mobile responsiveness */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2.5rem",
                marginBottom: "2rem",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Response Summary Stats - Mobile responsive grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "1rem",
                  marginBottom: "2rem",
                  padding: isMobile ? "1rem" : "1.5rem",
                  backgroundColor: "rgba(201, 167, 80, 0.1)",
                  borderRadius: "12px",
                }}
              >
                {/* Stats cards with responsive text sizes */}
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#c9a750ff",
                    }}
                  >
                    📊
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {results.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Questions Answered
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#10b981",
                    }}
                  >
                    ⚡
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {
                      recommendations.filter((r) => r.priority === "High")
                        .length
                    }
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    High Priority Features
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#f59e0b",
                    }}
                  >
                    🎯
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {Math.round(
                      recommendations.reduce((acc, r) => acc + r.score, 0) /
                        recommendations.length
                    )}
                    %
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Avg. Match Score
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#8b5cf6",
                    }}
                  >
                    💼
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {categories.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Business Areas Covered
                  </div>
                </div>
              </div>

              {/* Individual responses with mobile optimization */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: isMobile ? "1rem" : "1.5rem",
                }}
              >
                {results.map((result, index) => (
                  <div
                    key={index}
                    style={{
                      padding: isMobile ? "1.5rem" : "2rem",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "16px",
                      border: "1px solid rgba(201, 167, 80, 0.2)",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    {/* Question header with responsive layout */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "1.5rem",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#c9a750ff",
                          color: "white",
                          borderRadius: "50%",
                          width: isMobile ? "28px" : "32px",
                          height: isMobile ? "28px" : "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          fontWeight: "600",
                          marginRight: isMobile ? "0" : "1rem",
                          marginBottom: isMobile ? "1rem" : "0",
                          flexShrink: 0,
                          alignSelf: isMobile ? "center" : "flex-start",
                        }}
                      >
                        Q{index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5
                          style={{
                            fontWeight: "700",
                            color: "#1f2937",
                            marginBottom: "0.5rem",
                            fontSize: isMobile ? "1rem" : "1.1rem",
                            lineHeight: "1.4",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {result.question}
                        </h5>
                      </div>
                    </div>

                    {/* Answer and impact sections with responsive styling */}
                    <div
                      style={{
                        backgroundColor: "rgba(201, 167, 80, 0.1)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #c9a750ff",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Your Response:
                      </div>
                      <div
                        style={{
                          color: "#c9a750ff",
                          fontWeight: "600",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {result.answer}
                      </div>
                    </div>

                    {/* Impact Analysis */}
                    <div
                      style={{
                        backgroundColor: "rgba(16, 185, 129, 0.05)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #10b981",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Impact on Recommendations:
                      </div>
                      <div
                        style={{
                          color: "#059669",
                          fontSize: isMobile ? "0.9rem" : "0.95rem",
                          lineHeight: "1.5",
                        }}
                      >
                        {/* Dynamic impact analysis based on the answer */}
                        {result.answer.toLowerCase().includes("data") ||
                        result.answer.toLowerCase().includes("reporting")
                          ? "✓ Triggered strategic data analytics and reporting capabilities for enhanced decision-making"
                          : result.answer.toLowerCase().includes("timeline") ||
                            result.answer.toLowerCase().includes("milestones")
                          ? "✓ Recommended strategic planning and roadmap development methodologies"
                          : result.answer.toLowerCase().includes("process") ||
                            result.answer.toLowerCase().includes("operations")
                          ? "✓ Added strategic process optimization and operational efficiency capabilities"
                          : result.answer.toLowerCase().includes("customer") ||
                            result.answer.toLowerCase().includes("experience")
                          ? "✓ Included strategic customer experience enhancement technologies"
                          : result.answer.toLowerCase().includes("budget") ||
                            result.answer.toLowerCase().includes("investment")
                          ? "✓ Focused on strategic technology investment planning and budget optimization"
                          : "✓ Contributed to your comprehensive strategic technology roadmap and planning recommendations"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Responsive */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? "0.75rem" : "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            padding: isMobile ? "0 1rem" : "0",
          }}
        >
          <PDFExport
            results={results}
            recommendations={recommendations}
            resultsRef={resultsRef}
          />
          <button
            onClick={onRestart}
            style={{
              padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              border: "2px solid #c9a750ff",
              borderRadius: "12px",
              cursor: "pointer",
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c9a750ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
            }}
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
